import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";

import CustomCheckBox from "../common/CustomCheckBox";
import CustomButton from "../common/CustomButton";
import { getColumView, setCheckedCustomColumn, updateColumnBookmark, saveColumnBook, deleteCustomColumn } from "../../redux/reducers/drmCustomColumnSlice";

import clsx from "clsx";
import "./DialogCustomColumn.sass";


function DialogCustomColumn({ open, displayColumns, handleClose, dialogType }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [saveHeader, setSaveHeader] = useState({
    isBookMark: false,
    checked: false,
    name: "",
  });
  const [customHeaders, setCustomHeaders] = useState([]);
  const { selectedMenu } = useSelector((state) => state.selectedMenu);
  const { defaultColumns, customViews, isLoading, errorMessage, checkedColumn } = useSelector((state) => state.customColumn);
  const { userInfo } = useSelector((state) => state.user);

  const [bookmarkedIndex, setBookmarkedIndex] = useState(null);
  const [checkedIndex, setCheckedIndex] = useState(null);
  const [pendingSaveBookmark, setPendingSaveBookmark] = useState(false);

  const viewCategory = useMemo(
    () => (selectedMenu === "daily_maintenance" ? 2 : 1),
    [selectedMenu]
  );
  const viewType = useMemo(
    () => (selectedMenu === "daily_maintenance" ? "daily" : "bid"),
    [selectedMenu]
  );

  useEffect(() => {
    const defaultView = {
      viewName:
        selectedMenu === "daily_maintenance"
          ? "Default Daily View"
          : "Default Bid View",
      viewCategory: viewCategory,
      isBookMark: false,
      isDefault: true,
      tableDetails:
        defaultColumns[viewType]?.map((col) => ({
          columnName: col.columnName,
          priority: col.columnNumber,
        })) || [],
    };

    const views = customViews[viewType] || [];
    setCustomHeaders([defaultView, ...views]);
  }, [selectedMenu, customViews, defaultColumns, viewCategory, viewType]);

  useEffect(() => {
    const controller = new AbortController();
    const viewCategoryStr = selectedMenu === "daily_maintenance" ? "2" : "1";
    const body = {
      viewCodes: [],
      viewNames: [],
      viewCategory: [viewCategoryStr],
      isBookMark: [false, true],
      userCode: [userInfo?.userName],
    };
    dispatch(getColumView({ body, signal: controller.signal }));
    return () => {
      controller.abort();
    };
  }, [userInfo, selectedMenu]);

  useEffect(() => {
    if (!open) return;

    const currentCheckedView = checkedColumn[viewCategory];
    const bookmarked = customHeaders.findIndex((n) => n.isBookMark);

    if (bookmarked === -1 && customHeaders.length > 0) {
      setBookmarkedIndex(0);
    } else if (bookmarked !== -1) {
      setBookmarkedIndex(bookmarked);
    }

    if (currentCheckedView) {
      const checkedIdx = customHeaders.findIndex(
        (n) =>
          n.viewCode === currentCheckedView.viewCode ||
          (n.isDefault && currentCheckedView.isDefault)
      );
      setCheckedIndex(checkedIdx !== -1 ? checkedIdx : null);
    } else if (bookmarked !== -1) {
      setCheckedIndex(bookmarked);
    } else {
      setCheckedIndex(0);
    }
  }, [customHeaders, open, viewCategory, checkedColumn]);

  const handleBookmarkChange = (index) => {
    const newBookmarkIndex = index === bookmarkedIndex ? null : index;
    setBookmarkedIndex(newBookmarkIndex);
    if (
      newBookmarkIndex !== null &&
      !customHeaders[newBookmarkIndex]?.isDefault
    ) {
      setPendingSaveBookmark(true);
    }
  };

  const handleDelete = (index) => {
    const viewToDelete = customHeaders[index];

    const body = {
      viewCodes: [viewToDelete.viewCode || null],
      version: viewToDelete.version,
      userCode: [userInfo?.userName],
    };

    dispatch(deleteCustomColumn({ body, applyOnly: false }));

    if (isLoading === false && !errorMessage) {
      handleClose();
    }
  };

  const handleCheckChange = (index) => {
    const newCheckedIndex = index === checkedIndex ? null : index;
    setCheckedIndex(newCheckedIndex);
  };

  const handleNameChange = (e) => {
    setSaveHeader((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleCustomColumnSaveAndApply = () => {
    if (pendingSaveBookmark && bookmarkedIndex !== null) {
      const selectedView = customHeaders[bookmarkedIndex];

      const updatedViews = customHeaders
        .slice(1)
        .map((view) =>
          view.viewCode === selectedView.viewCode
            ? { ...view, isBookMark: true }
            : { ...view, isBookMark: false }
        );

      const body = {
        userCode: userInfo.userName,
        views: updatedViews,
      };

      if (!selectedView.isDefault) {
        dispatch(
          saveColumnBook({
            body,
            applyOnly: false,
          })
        );

        if (checkedIndex === bookmarkedIndex) {
          dispatch(
            setCheckedCustomColumn({
              view: selectedView,
              viewCategory: viewCategory,
            })
          );
        }
      }
      if (isLoading === false && !errorMessage) {
        handleClose();
      }
    }

    if (
      checkedIndex !== null &&
      (!pendingSaveBookmark || checkedIndex !== bookmarkedIndex)
    ) {
      const selectedView = customHeaders[checkedIndex];

      dispatch(
        setCheckedCustomColumn({
          view: selectedView,
          viewCategory: viewCategory,
        })
      );

      dispatch(
        updateColumnBookmark({
          viewCategory,
          viewCode: selectedView.viewCode || null,
          isBookMark: false,
          applyOnly: true,
          tableDetails: selectedView.tableDetails,
        })
      );
      handleClose();
    }
  };

  const handleCustomColumnSave = () => {
    const tableDetails = displayColumns
      .filter((col) => col.isVisible)
      .map((col, index) => ({
        columnName: col.columnName,
        priority: index + 1,
      }));

    const body = {
      userCode: userInfo?.userName,
      views: [
        {
          viewName:
            saveHeader.name != "" ? saveHeader.name : "User Custom View",
          viewCategory: viewCategory,
          isBookMark: false,
          tableDetails: tableDetails,
        },
       ...customHeaders.filter(n => n.viewCode != null),
      ],
    };
    if (tableDetails.length > 0) {
      dispatch(saveColumnBook({ body, applyOnly: false }));
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: "custom-dialog-paper" }}
    >
      <DialogTitle className="dialog-title">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar className="avatar">
            <ViewColumnIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" className="dialog-heading">
              {t("custom_view")}
            </Typography>
            <Typography variant="body2" className="dialog-sub-heading">
              {t("manage_table_view")}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "black" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1 }}>
        {dialogType === "custom_columns" ? (
          <Stack spacing={1.5}>
            {customHeaders.map((column, index) => (
              <Box
                key={index}
                className={clsx(
                  "main-box1",
                  checkedIndex === index ? "first-box-true" : "first-box-false"
                )}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box className="main-box2-column" />
                    <Box>
                      <Typography
                        variant="body1"
                        className="main-box2-column-text"
                      >
                        {column.viewName}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {!column.isDefault && (
                      <IconButton
                        data-testid={`bookmark-button-${index}`}
                        onClick={() => handleBookmarkChange(index)}
                        size="small"
                        className={clsx(
                          "main-box2-column-bookmark",
                          bookmarkedIndex === index
                            ? "icon-button-true"
                            : "icon-button-false"
                        )}
                      >
                        {bookmarkedIndex === index ? (
                          <BookmarkIcon fontSize="small" />
                        ) : (
                          <BookmarkBorderIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    <CustomCheckBox
                      datatestid="custom-checkbox"
                      checked={checkedIndex === index}
                      onChange={() => handleCheckChange(index)}
                      size="small"
                      className="icon-customButton"
                    />
                    {!column.isDefault && (
                      <IconButton
                        size="small"
                        className="icon-buttons"
                        onClick={() => handleDelete(index)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Box className="main-box3">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box className="main-box3-column" />
                <Box>
                  <TextField
                    value={saveHeader.name}
                    size="small"
                    placeholder={customHeaders[checkedIndex]?.viewName || t("enter_view_name")}
                    onChange={handleNameChange}
                    className="main-box3-text"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1.5, gap: 1 }}>
        <CustomButton
          onClick={handleClose}
          size="small"
          className="cancel"
          datatestid="cancel"
          btnType="nonary"
        >
          {t("cancel")}
        </CustomButton>
        {dialogType === "custom_columns" ? (
          <CustomButton
            onClick={handleCustomColumnSaveAndApply}
            size="small"
            className="confirm"
            datatestid="confirm"
            btnType="nonary"
          >
            {t("save_apply")}
          </CustomButton>
        ) : (
          <CustomButton
            onClick={handleCustomColumnSave}
            size="small"
            className="confirm"
            datatestid="confirm"
            btnType="nonary"
          >
            {t("save")}
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default DialogCustomColumn;
